// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentsList.test.tsx
//
// Which comments are ticked for a bulk Relevant / Not relevant (Figma
// November 2026, frame 0b, "Bulk selection"). Only comments still listed and
// still open to a verdict count, so a ticked one that a verdict or a filter
// took away is not judged again behind her back.
import { useCallback, useEffect, useMemo, useState } from "react";

export type ReviewBulkSelection = {
  /** The ticked comments, in list order. */
  selected: readonly string[];
  isSelected: (commentId: string) => boolean;
  toggle: (commentId: string, checked: boolean) => void;
  /** Ticks every selectable comment, or none. */
  toggleAll: (checked: boolean) => void;
  clear: () => void;
  /** The Select all box: ticked, part-ticked, or empty. */
  allState: boolean | "indeterminate";
};

export function useReviewBulkSelection(
  selectable: readonly string[],
): ReviewBulkSelection {
  const [ticked, setTicked] = useState<ReadonlySet<string>>(() => new Set());
  const selected = useMemo(
    () => selectable.filter((id) => ticked.has(id)),
    [selectable, ticked],
  );
  // why: drop a tick once its comment leaves the selectable set, or a comment
  // judged by hand comes back ticked when a tab switch lists it again.
  useEffect(() => {
    setTicked((was) => {
      const next = new Set(
        Array.from(was).filter((id) => selectable.includes(id)),
      );
      return next.size === was.size ? was : next;
    });
  }, [selectable]);
  const toggle = useCallback((commentId: string, checked: boolean) => {
    setTicked((was) => {
      const next = new Set(was);
      if (checked) next.add(commentId);
      else next.delete(commentId);
      return next;
    });
  }, []);
  const toggleAll = useCallback(
    (checked: boolean) => setTicked(new Set(checked ? selectable : [])),
    [selectable],
  );
  const clear = useCallback(() => setTicked(new Set()), []);
  const allState =
    selected.length === 0
      ? false
      : selected.length === selectable.length
        ? true
        : "indeterminate";
  return {
    selected,
    isSelected: (commentId) => selected.includes(commentId),
    toggle,
    toggleAll,
    clear,
    allState,
  };
}
